import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const NETWORK_DETAILS: Record<string, {
  id: string; title: string; subtitle: string; color: string; icon: string;
  heroImg: string; overview: string;
  specs: { label: string; value: string }[];
  features: { title: string; desc: string; detail: string; icon: string; img: string }[];
  roadmap: { year: string; event: string; status: 'done' | 'active' | 'future' }[];
  gallery: string[];
  stats: { label: string; value: string; icon: string }[];
  techStack: string[];
  comparison?: { label: string; prev: string; current: string; next: string }[];
}> = {
  'web-1': {
    id: 'web-1', title: 'Web 1.0', subtitle: '静态只读时代 · 1991–2004', color: '#A8B5C8', icon: 'ri-file-text-line',
    heroImg: 'https://public.readdy.ai/ai/img_res/cda800886c979b508d9af89b49ea75e2.jpg',
    overview: 'Web 1.0是互联网的原始形态，由Tim Berners-Lee于1991年在欧洲核子研究中心（CERN）发明。这个时代的互联网以静态HTML页面为主，用户只能"读"，无法"写"。内容由少数网站主控制，超链接是唯一的导航方式。\n\nWeb 1.0的核心特征是"信息单向流动"——从服务器到用户，没有反向通道。这个时代的互联网就像一本巨大的百科全书，你可以查阅，但不能编辑。尽管如此，Web 1.0完成了互联网从军用走向民用的历史性跨越，让信息民主化成为可能。\n\n雅虎、网景、AltaVista等早期互联网公司在这个时代崛起，互联网泡沫在2000年破裂，但也为Web 2.0的到来奠定了基础。',
    techStack: ['HTML 1.0/2.0', 'HTTP/1.0', 'TCP/IP协议', 'DNS域名系统', 'FTP文件传输', 'CGI动态脚本', '静态服务器', 'Mosaic/Netscape浏览器'],
    specs: [
      { label: '时代', value: '1991–2004' },
      { label: '核心特征', value: '静态只读' },
      { label: '用户角色', value: '被动接受者' },
      { label: '数据主权', value: '服务器端控制' },
      { label: '商业模式', value: '门户广告' },
      { label: '代表产品', value: '雅虎、网景' },
    ],
    features: [
      {
        title: 'HTML静态页面', desc: '纯HTML构建的静态网页，内容固定，无法动态更新',
        detail: 'Web 1.0的页面完全由HTML标签构成，没有JavaScript交互，没有CSS样式（早期），没有数据库支持。每个页面都是一个独立的HTML文件，存储在服务器上。用户访问时，服务器直接返回HTML文件，浏览器渲染显示。这种架构极其简单，但也极其脆弱——任何内容更新都需要手动修改HTML文件并重新上传。',
        icon: 'ri-file-text-line',
        img: 'https://readdy.ai/api/search-image?query=retro%20HTML%20webpage%201990s%20computer%20screen%20static%20website%20silver%20gray%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web1-f1&orientation=landscape',
      },
      {
        title: '门户网站模式', desc: '雅虎、网景等门户网站成为互联网入口，流量高度集中',
        detail: '门户网站是Web 1.0时代的核心商业模式。雅虎、AOL、网景等公司通过提供搜索、新闻、邮件等综合服务，成为用户进入互联网的"大门"。这些门户网站的流量极度集中，广告收入是主要商业模式。1999年，雅虎市值超过1000亿美元，成为互联网泡沫的象征。',
        icon: 'ri-door-open-line',
        img: 'https://readdy.ai/api/search-image?query=portal%20website%201990s%20Yahoo%20AOL%20internet%20gateway%20retro%20silver%20gray%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web1-f2&orientation=landscape',
      },
      {
        title: '互联网泡沫', desc: '1995-2000年互联网泡沫，纳斯达克指数暴涨后崩溃',
        detail: '互联网泡沫是Web 1.0时代最重要的历史事件。1995-2000年，大量互联网公司在没有盈利模式的情况下获得巨额融资，纳斯达克指数从1000点飙升至5000点。2000年3月，泡沫破裂，纳斯达克在18个月内跌去78%，数千家互联网公司倒闭。但这次泡沫也留下了宝贵遗产：互联网基础设施大幅扩张，为Web 2.0的爆发奠定了基础。',
        icon: 'ri-stock-line',
        img: 'https://readdy.ai/api/search-image?query=dot-com%20bubble%20internet%20crash%202000%20stock%20market%20silver%20gray%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web1-f3&orientation=landscape',
      },
      {
        title: 'HTTP协议诞生', desc: 'Tim Berners-Lee发明HTTP协议，奠定互联网通信基础',
        detail: 'HTTP（超文本传输协议）是Web的基础通信协议，由Tim Berners-Lee于1989年提出，1991年正式发布。HTTP定义了客户端（浏览器）和服务器之间的通信规则，让不同计算机上的文档可以相互链接。HTTP/1.0（1996年）支持持久连接，HTTP/1.1（1997年）引入管道化，这些改进大幅提升了Web性能，为Web 2.0的到来奠定了技术基础。',
        icon: 'ri-code-line',
        img: 'https://readdy.ai/api/search-image?query=HTTP%20protocol%20internet%20communication%20network%20code%20silver%20gray%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web1-f4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '1989', event: 'Tim Berners-Lee提出万维网概念，发明HTTP和HTML', status: 'done' },
      { year: '1991', event: '第一个网站上线（info.cern.ch），Web 1.0正式诞生', status: 'done' },
      { year: '1993', event: 'Mosaic浏览器发布，图形化界面让互联网走向大众', status: 'done' },
      { year: '1995', event: '雅虎、亚马逊、eBay相继成立，互联网商业化开始', status: 'done' },
      { year: '2000', event: '互联网泡沫破裂，纳斯达克暴跌78%', status: 'done' },
      { year: '2004', event: 'Facebook成立，Web 2.0时代开启，Web 1.0落幕', status: 'done' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=1990s%20computer%20internet%20retro%20silver%20gray%20dark%20background%208k&width=400&height=300&seq=net-web1-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Yahoo%20portal%20website%201990s%20retro%20silver%20gray%20dark%20background%208k&width=400&height=300&seq=net-web1-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Netscape%20browser%201990s%20retro%20silver%20gray%20dark%20background%208k&width=400&height=300&seq=net-web1-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=dot-com%20era%20internet%202000%20silver%20gray%20dark%20background%208k&width=400&height=300&seq=net-web1-g4&orientation=landscape',
    ],
    stats: [
      { label: '全球网站数', value: '1700万', icon: 'ri-global-line' },
      { label: '互联网用户', value: '5亿', icon: 'ri-user-line' },
      { label: '峰值市值', value: '$6.7万亿', icon: 'ri-money-dollar-circle-line' },
      { label: '持续时间', value: '13年', icon: 'ri-time-line' },
    ],
  },
  'web-3': {
    id: 'web-3', title: 'Web 3.0', subtitle: '去中心化时代 · 2020–2026', color: '#00FF88', icon: 'ri-links-line',
    heroImg: 'https://readdy.ai/api/search-image?query=blockchain%20decentralized%20network%20nodes%20glowing%20green%20neon%20web3%20cryptocurrency%20NFT%20DeFi%20digital%20art%20dark%20background%20futuristic%20cinematic%20wide%20panoramic%208k&width=1400&height=600&seq=net-web3-detail-hero&orientation=landscape',
    overview: 'Web 3.0以区块链为底层基础设施，实现"去信任化"的价值传输。这是互联网从"信息互联网"向"价值互联网"的历史性跨越。在Web 3.0中，用户不再需要信任中心化平台，智能合约自动执行协议，代码即法律。\n\nWeb 3.0的核心创新是"数字所有权"——通过NFT和区块链，数字资产第一次有了真正的所有权。你的数字艺术品、游戏道具、虚拟土地，都可以像真实资产一样被拥有、转让和交易，不受任何平台控制。\n\nDeFi（去中心化金融）重构了金融体系，让任何人都可以在没有银行的情况下借贷、交易和赚取收益。DAO（去中心化自治组织）创造了新的组织形式，让全球协作成为可能。',
    techStack: ['以太坊 Ethereum', 'Solana高性能链', 'IPFS分布式存储', 'Layer2扩容方案', '智能合约 Solidity', 'NFT标准 ERC-721', 'DeFi协议', '零知识证明 ZKP'],
    specs: [
      { label: '时代', value: '2020–2026' },
      { label: '核心特征', value: '去中心化' },
      { label: '用户角色', value: '资产所有者' },
      { label: '数据主权', value: '用户私钥控制' },
      { label: '商业模式', value: 'Token经济' },
      { label: '代表产品', value: 'Ethereum/OpenSea' },
    ],
    features: [
      {
        title: '区块链基础设施', desc: '以太坊、Solana等公链构成Web 3.0的底层基础设施',
        detail: '区块链是Web 3.0的核心基础设施，提供去中心化的数据存储和计算能力。以太坊是最重要的智能合约平台，支持DeFi、NFT、DAO等所有Web 3.0应用。Solana以高性能著称，每秒可处理65000笔交易，是以太坊的100倍。Layer2扩容方案（如Arbitrum、Optimism）在以太坊之上构建高性能层，大幅降低交易成本。',
        icon: 'ri-links-line',
        img: 'https://readdy.ai/api/search-image?query=blockchain%20Ethereum%20network%20nodes%20glowing%20green%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web3-f1&orientation=landscape',
      },
      {
        title: 'NFT数字所有权', desc: 'NFT赋予数字资产唯一性和所有权，创造数字经济新范式',
        detail: 'NFT（非同质化代币）是Web 3.0最具革命性的创新之一。通过区块链技术，每个NFT都是唯一的、不可复制的数字资产，可以代表艺术品、音乐、游戏道具、虚拟土地等任何数字内容。2021年，NFT市场年交易额超过400亿美元，Beeple的数字艺术品以6900万美元成交，创下数字艺术品拍卖纪录。',
        icon: 'ri-nft-line',
        img: 'https://readdy.ai/api/search-image?query=NFT%20digital%20art%20ownership%20blockchain%20token%20glowing%20green%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web3-f2&orientation=landscape',
      },
      {
        title: 'DeFi去中心化金融', desc: 'DeFi重构金融体系，让任何人都可以在没有银行的情况下参与金融活动',
        detail: 'DeFi（去中心化金融）是Web 3.0最重要的应用场景。通过智能合约，用户可以在没有银行、交易所等中介机构的情况下进行借贷、交易、理财等金融活动。DeFi协议的总锁仓量（TVL）峰值超过2000亿美元，Uniswap等去中心化交易所的日交易量超过传统中心化交易所。',
        icon: 'ri-bank-line',
        img: 'https://readdy.ai/api/search-image?query=DeFi%20decentralized%20finance%20blockchain%20smart%20contract%20glowing%20green%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web3-f3&orientation=landscape',
      },
      {
        title: 'DAO去中心化组织', desc: 'DAO创造了新的组织形式，让全球协作在没有中心化管理的情况下成为可能',
        detail: 'DAO（去中心化自治组织）是Web 3.0对组织形式的革命性创新。DAO通过智能合约和代币投票机制，让全球任何地方的人都可以参与组织决策，无需中心化管理层。ConstitutionDAO在48小时内从全球4万人筹集了4700万美元，试图购买美国宪法原件。MakerDAO管理着数十亿美元的去中心化稳定币系统。',
        icon: 'ri-group-line',
        img: 'https://readdy.ai/api/search-image?query=DAO%20decentralized%20autonomous%20organization%20governance%20voting%20glowing%20green%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web3-f4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2008', event: '中本聪发布比特币白皮书，区块链技术诞生', status: 'done' },
      { year: '2015', event: '以太坊主网上线，智能合约平台建立', status: 'done' },
      { year: '2020', event: 'DeFi Summer爆发，TVL从10亿增长至150亿美元', status: 'done' },
      { year: '2021', event: 'NFT市场爆发，年交易额超400亿，元宇宙概念引爆', status: 'done' },
      { year: '2022', event: '以太坊完成PoS合并，能耗降低99.95%', status: 'done' },
      { year: '2025', event: 'Web 3.0基础设施成熟，与Web 4.0融合开始', status: 'active' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=Ethereum%20blockchain%20network%20green%20neon%20dark%20background%208k&width=400&height=300&seq=net-web3-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=NFT%20digital%20art%20collection%20green%20neon%20dark%20background%208k&width=400&height=300&seq=net-web3-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=DeFi%20protocol%20liquidity%20pool%20green%20neon%20dark%20background%208k&width=400&height=300&seq=net-web3-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=DAO%20governance%20voting%20blockchain%20green%20neon%20dark%20background%208k&width=400&height=300&seq=net-web3-g4&orientation=landscape',
    ],
    stats: [
      { label: 'DeFi峰值TVL', value: '$2000亿', icon: 'ri-bank-line' },
      { label: 'NFT年交易额', value: '$400亿', icon: 'ri-nft-line' },
      { label: '区块链开发者', value: '400万+', icon: 'ri-code-line' },
      { label: '全球钱包用户', value: '3亿+', icon: 'ri-wallet-line' },
    ],
  },
  '6g': {
    id: '6g', title: '6G网络', subtitle: '元宇宙基础设施 · 2025–2030', color: '#00FF88', icon: 'ri-signal-tower-fill',
    heroImg: 'https://readdy.ai/api/search-image?query=6G%20terahertz%20network%20futuristic%20green%20neon%20glowing%20signal%20waves%20space%20satellite%20earth%20coverage%20technology%20dark%20background%20sci-fi%20cinematic%20wide%20panoramic%208k%20ultra%20detailed&width=1400&height=600&seq=net-6g-detail-hero&orientation=landscape',
    overview: '6G是第六代移动通信技术，工作在太赫兹（THz）频段，峰值速率达1Tbps，是5G的100倍。6G不仅仅是速度的提升，更是通信范式的革命——从"连接人和设备"升级为"连接物理世界和数字世界"。\n\n6G的核心创新是"AI原生网络架构"——网络本身具备智能，可以自主学习、自主优化、自主修复。每个基站都是一个AI节点，可以根据用户行为预测流量需求，提前分配资源。这让6G网络的效率比5G提升10倍以上。\n\n空天地海一体化是6G的另一个重要特征。通过将卫星、无人机、地面基站和海洋浮标整合为一个统一的网络，6G实现了真正的全球无缝覆盖，消除了地球上最后的通信盲区。',
    techStack: ['太赫兹通信（0.1-10THz）', 'AI原生网络架构', '空天地海一体化', '智能超表面RIS', '语义通信技术', '量子安全加密', '边缘智能计算', '数字孪生网络'],
    specs: [
      { label: '峰值速率', value: '1 Tbps' },
      { label: '端到端延迟', value: '<0.1ms' },
      { label: '连接密度', value: '1000万/km²' },
      { label: '频谱范围', value: '0.1-10 THz' },
      { label: '能效提升', value: '100倍于5G' },
      { label: '商用时间', value: '2025年' },
    ],
    features: [
      {
        title: '太赫兹通信', desc: '工作在0.1-10THz频段，峰值速率1Tbps，是5G的100倍',
        detail: '太赫兹（THz）频段是6G的核心频谱资源，位于微波和红外线之间。太赫兹波的频率极高，可以携带海量数据，理论峰值速率达到1Tbps。但太赫兹波也有明显缺点：穿透力弱，容易被空气中的水分子吸收，传播距离短。为了克服这些限制，6G采用了"智能超表面"（RIS）技术，通过在建筑物表面部署可编程反射面，将太赫兹信号绕过障碍物传播。',
        icon: 'ri-signal-tower-fill',
        img: 'https://readdy.ai/api/search-image?query=terahertz%206G%20signal%20wave%20technology%20green%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-6g-f1&orientation=landscape',
      },
      {
        title: 'AI原生网络', desc: '网络本身具备AI智能，可以自主学习、优化和修复',
        detail: 'AI原生网络是6G最革命性的架构创新。在5G中，AI是网络管理的辅助工具；在6G中，AI是网络的核心组成部分。每个6G基站都内置了AI芯片，可以实时分析用户行为，预测流量需求，自动优化资源分配。网络可以自主检测故障并修复，无需人工干预。AI原生网络让6G的运营成本比5G降低50%，同时效率提升10倍。',
        icon: 'ri-brain-line',
        img: 'https://readdy.ai/api/search-image?query=AI%20native%20network%20intelligent%20base%20station%20green%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-6g-f2&orientation=landscape',
      },
      {
        title: '空天地海一体化', desc: '卫星、无人机、地面基站、海洋浮标整合为统一网络',
        detail: '空天地海一体化是6G实现全球无缝覆盖的关键。系统由四层组成：太空层（低轨卫星星座）、空中层（高空无人机基站）、地面层（传统基站）、海洋层（海洋浮标基站）。四层协同工作，确保地球任何位置都有网络覆盖，包括南极洲、深海和高空。这让6G成为真正意义上的"全球网络"，彻底消除数字鸿沟。',
        icon: 'ri-earth-line',
        img: 'https://readdy.ai/api/search-image?query=6G%20satellite%20ground%20sea%20air%20integrated%20network%20coverage%20green%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-6g-f3&orientation=landscape',
      },
      {
        title: '语义通信', desc: '传输"意义"而非"比特"，通信效率提升1000倍',
        detail: '语义通信是6G最前沿的技术方向，也是通信技术的范式革命。传统通信传输的是比特流（0和1），接收方需要重新解码；语义通信传输的是"意义"——AI在发送端提取信息的语义，在接收端重建信息，而不是逐比特传输。这让通信效率提升1000倍以上。例如，传输一段视频，语义通信只需传输"一个人在公园里跑步"这个语义，接收端AI自动生成对应的视频。',
        icon: 'ri-translate-2',
        img: 'https://readdy.ai/api/search-image?query=semantic%20communication%20AI%20meaning%20transmission%20green%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-6g-f4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2019', event: '5G商用，6G研究正式启动', status: 'done' },
      { year: '2021', event: '中国、美国、欧盟、日本相继启动6G国家计划', status: 'done' },
      { year: '2023', event: '6G关键技术（太赫兹、RIS）实验室验证完成', status: 'done' },
      { year: '2025', event: '6G标准冻结，首批商用设备发布', status: 'active' },
      { year: '2027', event: '6G在主要城市规模部署，覆盖率达50%', status: 'future' },
      { year: '2030', event: '6G全球覆盖，空天地海一体化网络建成', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=6G%20base%20station%20tower%20green%20neon%20dark%20background%208k&width=400&height=300&seq=net-6g-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=terahertz%20chip%20processor%20green%20neon%20dark%20background%208k&width=400&height=300&seq=net-6g-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=satellite%20constellation%20orbit%20earth%20green%20neon%20dark%20background%208k&width=400&height=300&seq=net-6g-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=AI%20network%20management%20intelligent%20green%20neon%20dark%20background%208k&width=400&height=300&seq=net-6g-g4&orientation=landscape',
    ],
    stats: [
      { label: '峰值速率', value: '1 Tbps', icon: 'ri-speed-line' },
      { label: '延迟', value: '<0.1ms', icon: 'ri-timer-flash-line' },
      { label: '全球专利', value: '12万+', icon: 'ri-award-line' },
      { label: '商用时间', value: '2025年', icon: 'ri-calendar-line' },
    ],
  },
  'web-5': {
    id: 'web-5', title: 'Web 5.0', subtitle: '虚实融合时代 · 2030+', color: '#00FFCC', icon: 'ri-global-line',
    heroImg: 'https://readdy.ai/api/search-image?query=web5%20metaverse%20virtual%20reality%20brain%20computer%20interface%20quantum%20holographic%20world%20cyan%20neon%20glow%20dark%20background%20futuristic%20cinematic%20wide%20panoramic%208k%20ultra%20detailed&width=1400&height=600&seq=net-web5-detail-hero&orientation=landscape',
    overview: 'Web 5.0是Jack Dorsey提出的"去中心化网络+数字身份主权"终极形态，也是META66元宇宙愿景的技术基础。在Web 5.0中，虚拟与现实的边界彻底消融，人类在两个世界中自由穿梭，数字身份与物理身份完全统一。\n\nWeb 5.0的核心是"数字主权三角"：去中心化身份（DID）让每个人完全控制自己的数字身份；去中心化数据（DWN）让每个人完全控制自己的数据；去中心化应用（DApp）让每个人不受平台控制地使用应用。这三者共同构成了真正的数字主权。\n\n结合脑机接口、量子通信和全息投影，Web 5.0将实现人类历史上最深刻的技术革命——意识与互联网的直接连接，让人类成为真正的"数字-物理双栖"物种。',
    techStack: ['去中心化身份DID', '去中心化数据DWN', '量子通信协议', '脑机接口标准', '全息投影技术', '意识数字化编码', '零知识证明', '跨链互操作协议'],
    specs: [
      { label: '时代', value: '2030+' },
      { label: '核心特征', value: '虚实融合' },
      { label: '用户角色', value: '数字-物理双栖' },
      { label: '数据主权', value: '个人完全控制' },
      { label: '商业模式', value: '意识经济' },
      { label: '代表产品', value: 'META66元宇宙' },
    ],
    features: [
      {
        title: '去中心化身份DID', desc: '用户完全控制自己的数字身份，无需依赖任何平台',
        detail: '去中心化身份（DID）是Web 5.0的核心基础设施。每个用户拥有一个由自己私钥控制的数字身份，可以在任何平台使用，不受任何公司控制。DID支持可验证凭证（VC），让用户可以证明自己的身份、学历、资质等，而不需要透露不必要的个人信息。这彻底解决了互联网上的身份问题，让"一个身份走遍所有平台"成为现实。',
        icon: 'ri-fingerprint-line',
        img: 'https://readdy.ai/api/search-image?query=decentralized%20identity%20DID%20digital%20sovereignty%20cyan%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web5-f1&orientation=landscape',
      },
      {
        title: '脑机接口集成', desc: '脑机接口与Web 5.0深度集成，实现意念上网',
        detail: 'Web 5.0与脑机接口的融合是人类技术史上最深刻的革命。通过非侵入式BCI设备，用户可以用意念直接与互联网交互——想到一个问题，答案就出现在视野中；想到一个人，就可以立即与他建立连接。META66正在开发"意识协议"，定义脑机接口与Web 5.0的标准接口，让不同厂商的BCI设备都能无缝接入元宇宙。',
        icon: 'ri-mental-health-line',
        img: 'https://readdy.ai/api/search-image?query=brain%20computer%20interface%20web5%20neural%20connection%20internet%20cyan%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web5-f2&orientation=landscape',
      },
      {
        title: '全息空间互联网', desc: '互联网内容以全息形式投影到真实空间，虚实边界消融',
        detail: '全息空间互联网是Web 5.0最直观的体验升级。通过AR/MR设备或裸眼全息投影，互联网内容不再局限于屏幕，而是以三维全息形式投影到真实空间中。你的朋友可以以全息形象出现在你的客厅；你可以在真实的桌子上操作虚拟的3D模型；新闻可以以全息场景的形式在你面前重现。这让互联网从"二维屏幕"升级为"三维空间"。',
        icon: 'ri-layout-3-line',
        img: 'https://readdy.ai/api/search-image?query=holographic%20internet%20spatial%20web5%203D%20projection%20real%20world%20cyan%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web5-f3&orientation=landscape',
      },
      {
        title: '意识经济', desc: '人类意识成为最有价值的数字资产，意识经济规模超越实体经济',
        detail: '意识经济是Web 5.0最深刻的经济变革。在Web 5.0中，人类的注意力、情感、创意和意识本身成为最有价值的数字资产。通过意识数字化技术，人类的思维过程可以被记录、分析和货币化。创作者可以出售自己的"思维体验"，让他人以第一人称体验自己的创作过程。意识经济的规模预计在2040年超过实体经济，成为人类最大的经济形态。',
        icon: 'ri-brain-line',
        img: 'https://readdy.ai/api/search-image?query=consciousness%20economy%20digital%20mind%20value%20web5%20cyan%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=net-web5-f4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2021', event: 'Jack Dorsey提出Web 5.0概念，TBD公司成立', status: 'done' },
      { year: '2025', event: 'DID标准W3C认证，去中心化身份生态初步建立', status: 'active' },
      { year: '2028', event: '脑机接口与Web 5.0标准接口发布', status: 'future' },
      { year: '2030', event: 'Web 5.0基础设施成熟，全息互联网开始普及', status: 'future' },
      { year: '2035', event: '意识协议标准化，意识经济正式形成', status: 'future' },
      { year: '2040', event: 'Web 5.0全面普及，虚实边界彻底消融', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=web5%20holographic%20interface%20future%20internet%20cyan%20neon%20dark%20background%208k&width=400&height=300&seq=net-web5-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=DID%20decentralized%20identity%20digital%20sovereignty%20cyan%20neon%20dark%20background%208k&width=400&height=300&seq=net-web5-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=brain%20computer%20interface%20neural%20web%20connection%20cyan%20neon%20dark%20background%208k&width=400&height=300&seq=net-web5-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=consciousness%20economy%20digital%20mind%20value%20cyan%20neon%20dark%20background%208k&width=400&height=300&seq=net-web5-g4&orientation=landscape',
    ],
    stats: [
      { label: '预计市场规模', value: '$80万亿', icon: 'ri-money-dollar-circle-line' },
      { label: '意识经济规模', value: '超实体经济', icon: 'ri-brain-line' },
      { label: '全球用户', value: '80亿', icon: 'ri-user-line' },
      { label: '实现时间', value: '2040年', icon: 'ri-calendar-line' },
    ],
  },
};

export default function NetworkDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'features' | 'roadmap' | 'gallery'>('overview');
  const [selectedFeature, setSelectedFeature] = useState<number | null>(null);
  const [animIn, setAnimIn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const detail = id ? NETWORK_DETAILS[id] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setAnimIn(true), 100);
    if (detail) {
      document.title = `${detail.title} | META66 网络演进深度解析 · ${detail.subtitle}`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `META66 ${detail.title}底层逻辑深度解析：${detail.overview.slice(0, 120)}...`);
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', `${detail.title}, ${detail.subtitle}, META66, 互联网演进, ${detail.techStack.slice(0, 3).join(', ')}`);
    }
  }, [id, detail]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !detail) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const nodes: { x: number; y: number; vx: number; vy: number; r: number; color: string }[] = [];
    for (let i = 0; i < 40; i++) {
      nodes.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3, r: Math.random() * 2 + 1, color: `${detail.color}40` });
    }
    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodes.forEach((n, i) => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx = -n.vx;
        if (n.y < 0 || n.y > canvas.height) n.vy = -n.vy;
        nodes.forEach((m, j) => {
          if (i >= j) return;
          const dx = n.x - m.x; const dy = n.y - m.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(n.x, n.y);
            ctx.lineTo(m.x, m.y);
            ctx.strokeStyle = `${detail.color}${Math.floor((1 - dist / 150) * 15).toString(16).padStart(2, '0')}`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = n.color;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, [id, detail]);

  if (!detail) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#020810' }}>
        <div className="text-center">
          <p className="text-white/50 text-xl mb-4">页面不存在</p>
          <button onClick={() => navigate('/network')} className="px-6 py-3 rounded-xl font-bold text-black cursor-pointer" style={{ background: '#00FF88' }}>返回网络页面</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: '深度概述', icon: 'ri-information-line' },
    { id: 'features', label: '核心特性', icon: 'ri-apps-line' },
    { id: 'roadmap', label: '发展路线', icon: 'ri-map-pin-time-line' },
    { id: 'gallery', label: '图片展示', icon: 'ri-image-line' },
  ] as const;

  const allDetails = Object.values(NETWORK_DETAILS);

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: '#020810', fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />

      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `linear-gradient(${detail.color}15 1px, transparent 1px), linear-gradient(90deg, ${detail.color}15 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 0%, ${detail.color}06 0%, transparent 50%)` }} />
      </div>

      {/* Header */}
      <div className="sticky top-0 z-50" style={{ background: 'rgba(2,8,16,0.95)', borderBottom: `1px solid ${detail.color}20`, backdropFilter: 'blur(20px)' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/network')} className="flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap hover:scale-105" style={{ color: 'rgba(255,255,255,0.5)' }}>
              <i className="ri-arrow-left-line" />
              <span className="text-sm">返回网络</span>
            </button>
            <span className="text-white/20">/</span>
            <span className="text-sm font-bold" style={{ color: detail.color }}>{detail.title}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center" style={{ background: `${detail.color}20`, border: `1.5px solid ${detail.color}50`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
              <i className={`${detail.icon} text-white text-sm`} />
            </div>
            <span className="font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.9rem' }}>NETWORK · {detail.title.toUpperCase()}</span>
          </div>
          <div className="text-sm" style={{ color: `${detail.color}70`, fontFamily: 'Caveat, cursive' }}>META66 · 底层逻辑</div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative z-10 h-[480px] overflow-hidden">
        <img src={detail.heroImg} alt={detail.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,8,16,0.2) 0%, rgba(2,8,16,0.5) 50%, rgba(2,8,16,1) 100%)' }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 30% 50%, ${detail.color}10 0%, transparent 60%)` }} />

        <div className="absolute inset-0 flex items-center px-8 md:px-16">
          <div style={{ opacity: animIn ? 1 : 0, transform: animIn ? 'translateX(0)' : 'translateX(-40px)', transition: 'all 0.9s ease' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-5" style={{ background: `${detail.color}15`, border: `1.5px solid ${detail.color}40`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: detail.color }} />
              <span className="text-sm font-bold" style={{ color: detail.color, fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>NETWORK DEEP DIVE · {detail.subtitle}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-4 leading-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(135deg, ${detail.color}, white)` }}>{detail.title}</span>
            </h1>
            <p className="text-white/55 text-lg max-w-2xl">{detail.subtitle}</p>
            <div className="flex flex-wrap gap-4 mt-6">
              {detail.stats.map(s => (
                <div key={s.label} className="px-4 py-2" style={{ background: 'rgba(2,8,16,0.7)', border: `1px solid ${detail.color}30`, borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px', backdropFilter: 'blur(10px)' }}>
                  <p className="font-black text-lg" style={{ color: detail.color, fontFamily: 'Orbitron, sans-serif' }}>{s.value}</p>
                  <p className="text-white/40 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky z-40" style={{ top: '65px', background: 'rgba(2,8,16,0.95)', borderBottom: `1px solid ${detail.color}15`, backdropFilter: 'blur(20px)' }}>
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex gap-1 py-3">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap cursor-pointer" style={{ background: activeTab === tab.id ? `${detail.color}15` : 'transparent', color: activeTab === tab.id ? detail.color : 'rgba(255,255,255,0.4)', border: `1px solid ${activeTab === tab.id ? detail.color + '40' : 'transparent'}` }}>
                <i className={tab.icon} />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-12">

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 flex flex-col gap-6">
              <div className="p-8 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${detail.color}15`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-xl mb-5 flex items-center gap-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <i className="ri-information-line" style={{ color: detail.color }} />深度概述
                </h3>
                {detail.overview.split('\n\n').map((para, i) => (
                  <p key={i} className="text-white/60 leading-relaxed text-base mb-4 last:mb-0">{para}</p>
                ))}
              </div>
              <div className="p-8 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${detail.color}15`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-xl mb-5 flex items-center gap-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <i className="ri-stack-line" style={{ color: detail.color }} />核心技术栈
                </h3>
                <div className="flex flex-wrap gap-3">
                  {detail.techStack.map(tech => (
                    <span key={tech} className="px-4 py-2 text-sm font-semibold" style={{ background: `${detail.color}10`, color: detail.color, border: `1px solid ${detail.color}25`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive', fontSize: '0.9rem' }}>{tech}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="p-6 rounded-2xl" style={{ background: `${detail.color}06`, border: `1.5px solid ${detail.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-lg mb-5" style={{ fontFamily: 'Orbitron, sans-serif' }}>关键参数</h3>
                {detail.specs.map(spec => (
                  <div key={spec.label} className="flex justify-between items-center py-3 border-b" style={{ borderColor: `${detail.color}10` }}>
                    <span className="text-white/50 text-sm">{spec.label}</span>
                    <span className="font-black text-sm" style={{ color: detail.color, fontFamily: 'Orbitron, sans-serif' }}>{spec.value}</span>
                  </div>
                ))}
              </div>
              <div className="p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-lg mb-4">探索其他版本</h3>
                {allDetails.filter(d => d.id !== detail.id).map(d => (
                  <button key={d.id} onClick={() => navigate(`/network/${d.id}`)} className="w-full flex items-center gap-3 p-3 rounded-xl mb-2 last:mb-0 cursor-pointer transition-all hover:-translate-y-0.5" style={{ background: `${d.color}06`, border: `1px solid ${d.color}15` }}>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ background: `${d.color}15`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                      <i className={`${d.icon} text-sm`} style={{ color: d.color }} />
                    </div>
                    <span className="text-white/60 text-sm font-medium">{d.title}</span>
                    <i className="ri-arrow-right-line ml-auto text-xs" style={{ color: d.color }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {detail.features.map((feature, i) => (
              <div key={feature.title} className="group rounded-2xl overflow-hidden cursor-pointer transition-all duration-400 hover:-translate-y-2" style={{ background: 'rgba(255,255,255,0.02)', border: `1.5px solid ${detail.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px', opacity: animIn ? 1 : 0, transform: animIn ? 'translateY(0)' : 'translateY(30px)', transition: `all 0.6s ease ${i * 100}ms` }} onClick={() => setSelectedFeature(selectedFeature === i ? null : i)}>
                <div className="relative h-52 overflow-hidden">
                  <img src={feature.img} alt={feature.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 30%, rgba(2,8,16,0.95) 100%)' }} />
                  <div className="absolute top-3 left-3 w-10 h-10 flex items-center justify-center" style={{ background: `${detail.color}20`, border: `1.5px solid ${detail.color}40`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                    <i className={`${feature.icon} text-lg`} style={{ color: detail.color }} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-white font-black text-lg mb-2">{feature.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-4">{feature.desc}</p>
                  {selectedFeature === i && (
                    <div className="p-4 rounded-xl mb-4" style={{ background: `${detail.color}08`, border: `1px solid ${detail.color}20` }}>
                      <p className="text-white/65 text-sm leading-relaxed">{feature.detail}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: detail.color }}>
                    <span>{selectedFeature === i ? '收起详情' : '查看深度解析'}</span>
                    <i className={selectedFeature === i ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'roadmap' && (
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-px" style={{ background: `linear-gradient(to bottom, ${detail.color}60, transparent)` }} />
              {detail.roadmap.map((item, i) => (
                <div key={item.year} className="flex items-start gap-6 mb-8 last:mb-0" style={{ opacity: animIn ? 1 : 0, transform: animIn ? 'translateX(0)' : 'translateX(-30px)', transition: `all 0.6s ease ${i * 100}ms` }}>
                  <div className="w-16 h-16 flex items-center justify-center flex-shrink-0 z-10 font-black text-sm" style={{ background: item.status === 'done' ? detail.color : item.status === 'active' ? `${detail.color}30` : 'rgba(255,255,255,0.05)', border: `2px solid ${item.status === 'future' ? 'rgba(255,255,255,0.1)' : detail.color}`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%', color: item.status === 'done' ? '#000' : item.status === 'active' ? detail.color : 'rgba(255,255,255,0.3)', fontFamily: 'Orbitron, sans-serif', fontSize: '0.7rem' }}>
                    {item.status === 'done' ? <i className="ri-check-line text-xl" /> : item.year.slice(2)}
                  </div>
                  <div className="flex-1 pt-3 pb-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-black text-xl" style={{ color: item.status === 'future' ? 'rgba(255,255,255,0.3)' : detail.color, fontFamily: 'Orbitron, sans-serif' }}>{item.year}</span>
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{ background: item.status === 'done' ? `${detail.color}20` : item.status === 'active' ? `${detail.color}15` : 'rgba(255,255,255,0.05)', color: item.status === 'done' ? detail.color : item.status === 'active' ? detail.color : 'rgba(255,255,255,0.2)', border: `1px solid ${item.status === 'future' ? 'rgba(255,255,255,0.08)' : detail.color + '30'}` }}>
                        {item.status === 'done' ? '已完成' : item.status === 'active' ? '进行中' : '规划中'}
                      </span>
                    </div>
                    <p className="text-white/60 text-base leading-relaxed">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {detail.gallery.map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden group cursor-pointer" style={{ height: '280px', border: `1.5px solid ${detail.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px', opacity: animIn ? 1 : 0, transform: animIn ? 'translateY(0)' : 'translateY(30px)', transition: `all 0.6s ease ${i * 100}ms` }}>
                <img src={img} alt={`${detail.title} ${i + 1}`} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `${detail.color}15` }} />
                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="px-3 py-1.5 text-xs font-bold" style={{ background: `${detail.color}20`, color: detail.color, border: `1px solid ${detail.color}40`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }}>{detail.title} · 图{i + 1}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
